import { useState, useEffect, useMemo } from 'react'
import { View, Text, Pressable, StyleSheet, ActivityIndicator } from 'react-native'
import { Mail, MailOpen, MessageSquare, CheckCircle, Clock, ChevronDown, ChevronUp, Search, ThumbsUp, Bell, Send } from 'lucide-react-native'
import { apiClient } from '../api/client'
import { storage, USER_ID_KEY } from '../utils/storage'
import { colors, spacing, borderRadius, typography } from '../theme'

interface Activity {
  type: string
  id: string
  title: string
  description: string
  date: string
  status: string
}

interface ActivityGroup {
  id: string
  label: string
  date: string
  type: 'hunt' | 'single'
  items: Activity[]
}

function groupActivities(activities: Activity[]): ActivityGroup[] {
  if (activities.length === 0) return []
  const groups: ActivityGroup[] = []
  let i = 0

  while (i < activities.length) {
    const current = activities[i]
    const isHuntEmail = current.title.includes('Tilbudsforespørgsel') || current.title.includes('Fuldmagt')

    if (isHuntEmail) {
      const batchStart = new Date(current.date).getTime()
      const batch: Activity[] = [current]
      let j = i + 1
      while (j < activities.length) {
        const next = activities[j]
        const nextTime = new Date(next.date).getTime()
        const isRelated = next.title.includes('Tilbudsforespørgsel') || next.title.includes('Fuldmagt') || next.title.includes('Velkommen')
        if (isRelated && Math.abs(nextTime - batchStart) < 60000) {
          batch.push(next)
          j++
        } else break
      }

      const categories = new Set<string>()
      for (const item of batch) {
        const match = item.title.match(/(?:Tilbudsforespørgsel|Fuldmagt).*?-\s*(.+)/)
        if (match) {
          match[1].split(',').forEach(c => {
            const trimmed = c.trim()
            if (trimmed && !trimmed.includes('Kunde')) categories.add(trimmed)
          })
        }
      }
      const catLabel = categories.size > 0 ? Array.from(categories).join(', ') : 'Tjenester'

      groups.push({ id: `hunt-${current.id}`, label: `Jagt: ${catLabel}`, date: current.date, type: 'hunt', items: batch })
      i = j
    } else {
      let cleanTitle = current.title.replace(/^Mail:\s*/, '')
      if (current.type !== 'quote_accepted') {
        cleanTitle = cleanTitle.replace(/\s*[—–]\s*[^—–]+$/, '')
      }
      groups.push({ id: `single-${current.id}`, label: cleanTitle || current.title, date: current.date, type: 'single', items: [current] })
      i++
    }
  }
  return groups
}

interface ActivityFeedProps {
  onLoad?: (count: number) => void
  inboxMessages?: any[]
}

export default function ActivityFeed({ onLoad, inboxMessages }: ActivityFeedProps) {
  const [activities, setActivities] = useState<Activity[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    loadActivities()
  }, [inboxMessages])

  const loadActivities = async () => {
    try {
      const userId = await storage.get(USER_ID_KEY)
      if (!userId) { setLoading(false); return }

      const feedRes = await apiClient.activity.getFeed(userId).catch(() => ({ data: [] }))

      const rawFeedData: Activity[] = feedRes.data || []
      const feedData = rawFeedData.filter(a => !a.title?.includes('Kundeaccept'))

      const acceptedItems: Activity[] = []
      {
        const msgs: any[] = inboxMessages || []
        const catMap: Record<string, string> = { energy: 'Strøm', mobile: 'Mobil', internet: 'Internet' }
        for (const m of msgs) {
          if (m.parsed_data?.type === 'quote_accepted') {
            const cat = m.parsed_data?.category
            acceptedItems.push({
              type: 'quote_accepted', id: `qa-${m.id}`,
              title: `Tilbud accepteret — ${m.parsed_data.provider || m.provider_name || ''}`,
              description: catMap[cat] || cat || 'Tilbud',
              date: m.received_at, status: 'accepted',
            })
          }
        }
      }

      const sorted = [...feedData, ...acceptedItems].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      )
      // Deduplicate consecutive items with same title (e.g. duplicate welcome emails)
      const merged = sorted.filter((item, i) => i === 0 || item.title !== sorted[i - 1].title)
      setActivities(merged)
      onLoad?.(merged.length)
    } catch (error) {
      console.error('Error loading activity feed', error)
      onLoad?.(0)
    } finally {
      setLoading(false)
    }
  }

  const groups = useMemo(() => groupActivities(activities), [activities])

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    )
  }

  if (activities.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Clock size={20} color={colors.text.muted} />
        <Text style={styles.emptyText}>Ingen aktivitet endnu. Vi holder øje.</Text>
      </View>
    )
  }

  const displayGroups = showAll ? groups : groups.slice(0, 4)

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Seneste Aktivitet</Text>
      </View>
      {displayGroups.map((group) => {
        const isExpanded = expandedGroup === group.id
        const dateLabel = new Date(group.date).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })

        if (group.type === 'hunt') {
          return (
            <View key={group.id}>
              <Pressable style={styles.activityRow} onPress={() => setExpandedGroup(isExpanded ? null : group.id)}>
                <View style={[styles.iconCircle, { backgroundColor: '#eff6ff', borderColor: '#bfdbfe' }]}>
                  <Search size={14} color={colors.primary} />
                </View>
                <View style={{ flex: 1 }}>
                  <View style={styles.titleRow}>
                    <Text style={styles.activityTitle} numberOfLines={1}>{group.label}</Text>
                    <Text style={styles.dateText}>{dateLabel}</Text>
                  </View>
                  <View style={styles.subRow}>
                    <Text style={styles.activityDesc}>{group.items.length} mails sendt</Text>
                    {isExpanded
                      ? <ChevronUp size={12} color={colors.text.muted} />
                      : <ChevronDown size={12} color={colors.text.muted} />}
                  </View>
                </View>
              </Pressable>
              {isExpanded && group.items.map(item => (
                <View key={item.id} style={styles.expandedItem}>
                  <Mail size={12} color={colors.text.muted} />
                  <Text style={styles.expandedItemText} numberOfLines={1}>
                    {item.title.replace(/^Mail:\s*/, '')}
                  </Text>
                </View>
              ))}
            </View>
          )
        }

        const item = group.items[0]
        const isAccepted = item.type === 'quote_accepted'
        const desc = item.type === 'email' && (!item.description || item.description === 'Email kommunikation')
          ? (item.title.includes('Tilbudsforespørgsel') ? 'Forespørgsel sendt til udbyder'
            : item.title.includes('Fuldmagt') ? 'Fuldmagt sendt til udbyder'
            : item.title.includes('Velkommen') ? 'Velkomstmail'
            : 'Indgående email')
          : item.description
        return (
          <View key={group.id} style={styles.activityRow}>
            <View style={[styles.iconCircle, isAccepted ? { backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' }
              : item.title.includes('Velkommen') ? { backgroundColor: '#fefce8', borderColor: '#fde68a' }
              : item.title.includes('Tilbudsforespørgsel') || item.title.includes('Fuldmagt') ? { backgroundColor: '#eff6ff', borderColor: '#bfdbfe' }
              : { backgroundColor: colors.surface, borderColor: colors.border }]}>
              {isAccepted && <ThumbsUp size={14} color={colors.success} />}
              {!isAccepted && item.type === 'email' && item.title.includes('Velkommen') && <Bell size={14} color="#ca8a04" />}
              {!isAccepted && item.type === 'email' && (item.title.includes('Tilbudsforespørgsel') || item.title.includes('Fuldmagt')) && <Send size={14} color={colors.primary} />}
              {!isAccepted && item.type === 'email' && !item.title.includes('Velkommen') && !item.title.includes('Tilbudsforespørgsel') && !item.title.includes('Fuldmagt') && <MailOpen size={14} color={colors.primary} />}
              {!isAccepted && item.type === 'ticket' && item.status === 'resolved' && <CheckCircle size={14} color={colors.success} />}
              {!isAccepted && item.type === 'ticket' && item.status !== 'resolved' && <MessageSquare size={14} color="#ca8a04" />}
              {!isAccepted && item.type !== 'email' && item.type !== 'ticket' && <Clock size={14} color={colors.text.muted} />}
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.titleRow}>
                <Text style={styles.activityTitle} numberOfLines={1}>{group.label}</Text>
                <Text style={styles.dateText}>{dateLabel}</Text>
              </View>
              <Text style={styles.activityDesc} numberOfLines={1}>{desc}</Text>
            </View>
          </View>
        )
      })}
      {groups.length > 4 && (
        <Pressable style={styles.showAllButton} onPress={() => setShowAll(!showAll)}>
          <Text style={styles.showAllText}>
            {showAll ? 'Vis mindre' : `Se alle ${groups.length} aktiviteter`}
          </Text>
          {showAll ? <ChevronUp size={12} color={colors.primary} /> : <ChevronDown size={12} color={colors.primary} />}
        </Pressable>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.background, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: colors.border, overflow: 'hidden',
  },
  header: {
    padding: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.borderLight,
    backgroundColor: colors.surface,
  },
  headerTitle: { ...typography.captionBold, color: colors.text.primary },
  loadingContainer: { padding: spacing.md, alignItems: 'center' },
  emptyContainer: {
    padding: spacing.lg, alignItems: 'center', gap: spacing.xs,
    backgroundColor: colors.background, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: colors.border,
  },
  emptyText: { ...typography.caption, color: colors.text.muted },
  activityRow: {
    flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm,
    paddingHorizontal: spacing.sm, paddingVertical: 10,
    borderBottomWidth: 1, borderBottomColor: colors.borderLight,
  },
  iconCircle: {
    width: 28, height: 28, borderRadius: 14, borderWidth: 1,
    alignItems: 'center', justifyContent: 'center', marginTop: 2,
  },
  titleRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 },
  activityTitle: { ...typography.captionBold, color: colors.text.primary, flex: 1 },
  dateText: { ...typography.small, color: colors.text.muted, flexShrink: 0 },
  subRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 2 },
  activityDesc: { ...typography.small, color: colors.text.muted },
  expandedItem: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingLeft: 48, paddingVertical: 6,
    backgroundColor: colors.surface, borderBottomWidth: 1, borderBottomColor: colors.borderLight,
  },
  expandedItemText: { ...typography.small, color: colors.text.muted, flex: 1 },
  showAllButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4,
    paddingVertical: 10,
  },
  showAllText: { ...typography.small, color: colors.primary, fontWeight: '500' },
})
